import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import KeyIcon from "@mui/icons-material/Key";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import {
  getAdminCustomers,
  createControlDeviceToken,
  dispatchControlCommand,
  getControlCommandById,
  getControlCommands,
  getControlDeviceById,
  getControlDevices,
  queueDeviceCommand,
  triggerDeviceQuickAction,
} from "services/controlCenterApi";

const QUICK_ACTIONS = [
  { action: "reboot", label: "Reboot" },
  { action: "restart_app", label: "App Neustart" },
  { action: "clear_app_cache", label: "App Cache leeren" },
  { action: "clear_webview_cache", label: "WebView Cache leeren" },
  { action: "ping", label: "Ping" },
  { action: "take_screenshot", label: "Screenshot" },
];

const COMMAND_STATUSES = ["", "QUEUED", "SENT", "ACK", "DONE", "FAILED"];

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asString(value) {
  return value === undefined || value === null || value === "" ? "-" : String(value);
}

function statusColor(status) {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "DONE" || normalized === "ACK") return "success";
  if (normalized === "FAILED") return "error";
  if (normalized === "SENT") return "info";
  if (normalized === "QUEUED") return "warning";
  return "default";
}

function getTelemetry(device) {
  const telemetry = device?.lastTelemetry || {};
  return {
    cpu: telemetry.cpuPct ?? telemetry.cpu ?? "-",
    memory: telemetry.memoryMb ?? telemetry.memory ?? "-",
    disk: telemetry.diskPct ?? telemetry.disk ?? "-",
    temp: telemetry.tempC ?? telemetry.temperature ?? "-",
  };
}

function buildCustomerOptions(devices) {
  const map = new Map();
  devices.forEach((device) => {
    if (!device?.customer?.id) return;
    map.set(device.customer.id, {
      id: device.customer.id,
      label: `${device.customer.companyName || "Unbekannt"} (${device.customer.customerNumber || "-"})`,
    });
  });
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
}

function resolveAssetUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (typeof window === "undefined") return url;
  return new URL(url, window.location.origin).toString();
}

function extractScreenshotUrl(command) {
  const candidates = [
    command?.result?.url,
    command?.result?.imageUrl,
    command?.result?.screenshotUrl,
    command?.result?.path,
    command?.payload?.url,
    command?.payload?.imageUrl,
    command?.payload?.screenshotUrl,
    command?.payload?.path,
  ];
  const found = candidates.find((value) => typeof value === "string" && value.trim());
  return found ? resolveAssetUrl(found) : "";
}

export default function ControlCenter() {
  const [customers, setCustomers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceAudits, setDeviceAudits] = useState([]);

  const [commands, setCommands] = useState([]);
  const [selectedCommandId, setSelectedCommandId] = useState("");
  const [selectedCommand, setSelectedCommand] = useState(null);

  const [commandCatalog, setCommandCatalog] = useState([]);

  const [onlineFilter, setOnlineFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [commandStatusFilter, setCommandStatusFilter] = useState("");

  const [tokenExpiresIn, setTokenExpiresIn] = useState("30d");
  const [generatedToken, setGeneratedToken] = useState(null);

  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingCommands, setLoadingCommands] = useState(false);
  const [loadingCommandDetail, setLoadingCommandDetail] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [liveFeedOpen, setLiveFeedOpen] = useState(false);
  const [liveFeedLoading, setLiveFeedLoading] = useState(false);
  const [liveFeedError, setLiveFeedError] = useState("");
  const [liveFeedImageUrl, setLiveFeedImageUrl] = useState("");
  const [liveFeedStatus, setLiveFeedStatus] = useState("");
  const [liveFeedUpdatedAt, setLiveFeedUpdatedAt] = useState("");
  const [liveFeedCommandId, setLiveFeedCommandId] = useState("");
  const liveFeedPollRef = useRef(null);

  const customerOptions = useMemo(() => {
    const fromDevices = buildCustomerOptions(devices);
    const merged = new Map(fromDevices.map((item) => [item.id, item]));
    customers.forEach((customer) => {
      if (!customer?.id) return;
      if (merged.has(customer.id)) return;
      merged.set(customer.id, {
        id: customer.id,
        label: `${customer.companyName || "Unbekannt"} (${customer.customerNumber || "-"})`,
      });
    });
    return Array.from(merged.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [customers, devices]);

  const loadDevices = useCallback(async () => {
    setLoadingDevices(true);
    setError("");
    try {
      const [devicesResponse, customersResponse] = await Promise.all([
        getControlDevices({
          online: onlineFilter || undefined,
          q: searchFilter || undefined,
          customerId: customerFilter || undefined,
          limit: 150,
        }),
        getAdminCustomers().catch(() => []),
      ]);

      const list = asArray(devicesResponse?.devices);
      const customerList = Array.isArray(customersResponse) ? customersResponse : asArray(customersResponse?.customers);
      setDevices(list);
      setCustomers(customerList);
      setCommandCatalog(asArray(devicesResponse?.commandCatalog));

      setSelectedDeviceId((current) => {
        if (current && list.some((d) => d.id === current)) return current;
        return list[0]?.id || "";
      });
    } catch (err) {
      setError(err.message || "Device-Liste konnte nicht geladen werden.");
      setDevices([]);
      setSelectedDeviceId("");
    } finally {
      setLoadingDevices(false);
    }
  }, [customerFilter, onlineFilter, searchFilter]);

  const loadDeviceDetails = useCallback(async (deviceId) => {
    if (!deviceId) {
      setSelectedDevice(null);
      setDeviceAudits([]);
      return;
    }

    setLoadingDetail(true);
    setError("");
    try {
      const response = await getControlDeviceById(deviceId);
      setSelectedDevice(response?.device || null);
      setDeviceAudits(asArray(response?.device?.audits));
      setCommandCatalog((prev) => {
        const incoming = asArray(response?.commandCatalog);
        if (!incoming.length) return prev;
        const merged = new Map();
        [...prev, ...incoming].forEach((item) => merged.set(item.name, item));
        return Array.from(merged.values());
      });
    } catch (err) {
      setError(err.message || "Device-Details konnten nicht geladen werden.");
      setSelectedDevice(null);
      setDeviceAudits([]);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const loadCommands = useCallback(async (deviceId) => {
    if (!deviceId) {
      setCommands([]);
      setSelectedCommandId("");
      return;
    }

    setLoadingCommands(true);
    setError("");
    try {
      const response = await getControlCommands({
        deviceId,
        status: commandStatusFilter || undefined,
        limit: 150,
      });
      const list = asArray(response?.commands);
      setCommands(list);
      setSelectedCommandId((current) => {
        if (current && list.some((cmd) => cmd.id === current)) return current;
        return list[0]?.id || "";
      });
      setCommandCatalog((prev) => {
        const incoming = asArray(response?.commandCatalog);
        if (!incoming.length) return prev;
        const merged = new Map();
        [...prev, ...incoming].forEach((item) => merged.set(item.name, item));
        return Array.from(merged.values());
      });
    } catch (err) {
      setError(err.message || "Command-Historie konnte nicht geladen werden.");
      setCommands([]);
      setSelectedCommandId("");
    } finally {
      setLoadingCommands(false);
    }
  }, [commandStatusFilter]);

  const loadCommandDetails = useCallback(async (commandId) => {
    if (!commandId) {
      setSelectedCommand(null);
      return;
    }

    setLoadingCommandDetail(true);
    setError("");
    try {
      const response = await getControlCommandById(commandId);
      setSelectedCommand(response || null);
    } catch (err) {
      setError(err.message || "Command-Detail konnte nicht geladen werden.");
      setSelectedCommand(null);
    } finally {
      setLoadingCommandDetail(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadDevices();
    }, 250);
    return () => clearTimeout(timeout);
  }, [loadDevices]);

  useEffect(() => {
    loadDeviceDetails(selectedDeviceId);
    loadCommands(selectedDeviceId);
    setGeneratedToken(null);
    setInfo("");
  }, [loadCommands, loadDeviceDetails, selectedDeviceId]);

  useEffect(() => {
    loadCommandDetails(selectedCommandId);
  }, [loadCommandDetails, selectedCommandId]);

  async function handleQuickAction(action) {
    if (!selectedDeviceId) return;
    setBusyAction(`quick:${action}`);
    setError("");
    setInfo("");
    try {
      await triggerDeviceQuickAction(selectedDeviceId, action);
      setInfo(`Quick Action '${action}' wurde ausgelöst.`);
      await loadCommands(selectedDeviceId);
      await loadDeviceDetails(selectedDeviceId);
    } catch (err) {
      setError(err.message || "Quick Action fehlgeschlagen.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleCatalogCommand(commandName, defaultPayload) {
    if (!selectedDeviceId) return;
    setBusyAction(`catalog:${commandName}`);
    setError("");
    setInfo("");
    try {
      await queueDeviceCommand(selectedDeviceId, {
        name: commandName,
        payload: defaultPayload || {},
        dispatchNow: true,
      });
      setInfo(`Command '${commandName}' wurde erstellt und dispatcht.`);
      await loadCommands(selectedDeviceId);
    } catch (err) {
      setError(err.message || "Command konnte nicht erstellt werden.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleDispatchAgain(commandId) {
    setBusyAction(`dispatch:${commandId}`);
    setError("");
    setInfo("");
    try {
      await dispatchControlCommand(commandId);
      setInfo("Command wurde erneut dispatcht.");
      await loadCommands(selectedDeviceId);
      await loadCommandDetails(commandId);
    } catch (err) {
      setError(err.message || "Re-Dispatch fehlgeschlagen.");
    } finally {
      setBusyAction("");
    }
  }

  async function handleGenerateToken() {
    if (!selectedDeviceId) return;
    setBusyAction("token");
    setError("");
    setInfo("");
    try {
      const created = await createControlDeviceToken(selectedDeviceId, {
        expiresIn: tokenExpiresIn || undefined,
      });
      setGeneratedToken(created);
      setInfo("Onboarding-Token wurde erzeugt.");
    } catch (err) {
      setError(err.message || "Token-Erzeugung fehlgeschlagen.");
    } finally {
      setBusyAction("");
    }
  }

  function stopLiveFeedPolling() {
    if (liveFeedPollRef.current) {
      clearInterval(liveFeedPollRef.current);
      liveFeedPollRef.current = null;
    }
  }

  async function refreshLiveFeed() {
    if (!selectedDeviceId) return;
    const response = await getControlCommands({
      deviceId: selectedDeviceId,
      limit: 30,
    });
    const list = asArray(response?.commands);
    const screenshotCommand = list.find((cmd) => String(cmd?.name || "").toLowerCase() === "takescreenshot");

    if (!screenshotCommand) {
      setLiveFeedStatus("warte auf Screenshot-Command");
      return;
    }

    setLiveFeedCommandId(screenshotCommand.id);
    setLiveFeedStatus(String(screenshotCommand.status || ""));
    setLiveFeedUpdatedAt(screenshotCommand.updatedAt || screenshotCommand.createdAt || "");

    const imageUrl = extractScreenshotUrl(screenshotCommand);
    if (imageUrl) {
      setLiveFeedImageUrl(imageUrl);
      setLiveFeedError("");
      if (String(screenshotCommand.status || "").toUpperCase() === "DONE") {
        stopLiveFeedPolling();
      }
      return;
    }

    if (String(screenshotCommand.status || "").toUpperCase() === "FAILED") {
      setLiveFeedError(screenshotCommand.errorMessage || "Screenshot fehlgeschlagen.");
      stopLiveFeedPolling();
    }
  }

  async function handleOpenLiveFeed() {
    if (!selectedDeviceId) return;
    setLiveFeedOpen(true);
    setLiveFeedLoading(true);
    setLiveFeedError("");
    setLiveFeedImageUrl("");
    setLiveFeedStatus("queued");
    setLiveFeedUpdatedAt("");
    setLiveFeedCommandId("");
    stopLiveFeedPolling();

    try {
      await triggerDeviceQuickAction(selectedDeviceId, "take_screenshot");
      await refreshLiveFeed();
      liveFeedPollRef.current = setInterval(() => {
        void refreshLiveFeed();
      }, 2500);
      setInfo("Live-Feed Screenshot wurde angefordert.");
      await loadCommands(selectedDeviceId);
      await loadDeviceDetails(selectedDeviceId);
    } catch (err) {
      setLiveFeedError(err.message || "Live-Feed konnte nicht gestartet werden.");
    } finally {
      setLiveFeedLoading(false);
    }
  }

  function handleCloseLiveFeed() {
    stopLiveFeedPolling();
    setLiveFeedOpen(false);
  }

  useEffect(() => () => stopLiveFeedPolling(), []);

  const telemetry = getTelemetry(selectedDevice);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5} mb={2}>
          <Box>
            <Typography variant="h4">Admin Control Center</Typography>
            <Typography variant="body2" color="text.secondary">
              Geräteverwaltung, Remote Actions, Command-Historie und Device-Onboarding.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadDevices} disabled={loadingDevices}>
            Refresh
          </Button>
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {info ? <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert> : null}

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" mb={1}>Device-Filter</Typography>
              <Stack spacing={1.25}>
                <FormControl fullWidth size="small">
                  <InputLabel id="online-filter-label">Online</InputLabel>
                  <Select
                    labelId="online-filter-label"
                    value={onlineFilter}
                    label="Online"
                    onChange={(event) => setOnlineFilter(event.target.value)}
                  >
                    <MenuItem value="">Alle</MenuItem>
                    <MenuItem value="true">Online</MenuItem>
                    <MenuItem value="false">Offline</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Suche"
                  placeholder="Name, Ort, IP, Kunde"
                  size="small"
                  value={searchFilter}
                  onChange={(event) => setSearchFilter(event.target.value)}
                />

                <FormControl fullWidth size="small">
                  <InputLabel id="customer-filter-label">Kunde</InputLabel>
                  <Select
                    labelId="customer-filter-label"
                    value={customerFilter}
                    label="Kunde"
                    onChange={(event) => setCustomerFilter(event.target.value)}
                  >
                    <MenuItem value="">Alle Kunden</MenuItem>
                    {customerOptions.map((customer) => (
                      <MenuItem key={customer.id} value={customer.id}>{customer.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Devices</Typography>
              {loadingDevices ? (
                <Box py={2} textAlign="center"><CircularProgress size={24} /></Box>
              ) : null}
              {!loadingDevices && devices.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Keine Devices gefunden.</Typography>
              ) : null}

              <Stack spacing={1}>
                {devices.map((device) => (
                  <Paper
                    key={device.id}
                    variant={selectedDeviceId === device.id ? "outlined" : "elevation"}
                    sx={{
                      p: 1.25,
                      cursor: "pointer",
                      borderColor: selectedDeviceId === device.id ? "primary.main" : "divider",
                    }}
                    onClick={() => setSelectedDeviceId(device.id)}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="subtitle2">{device.displayName || device.screen?.name || device.id}</Typography>
                      <Chip
                        size="small"
                        color={device.online ? "success" : "default"}
                        label={device.online ? "online" : "offline"}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Kunde: {device.customer?.companyName || "-"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last heartbeat: {formatDate(device.lastHeartbeatAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Commands/Audits: {device?._count?.commands ?? 0}/{device?._count?.audits ?? 0}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} mb={1}>
                <Typography variant="h6">Device-Details</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!selectedDeviceId || !!busyAction}
                  onClick={handleOpenLiveFeed}
                >
                  Live-Feed
                </Button>
              </Stack>
              {loadingDetail ? (
                <Box py={2} textAlign="center"><CircularProgress size={24} /></Box>
              ) : null}
              {!loadingDetail && !selectedDevice ? (
                <Typography variant="body2" color="text.secondary">Kein Device ausgewählt.</Typography>
              ) : null}

              {selectedDevice ? (
                <Grid container spacing={1.25}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Device-ID</Typography>
                    <Typography variant="body2">{selectedDevice.id}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Typography variant="body2">
                      <Chip
                        size="small"
                        color={selectedDevice.online ? "success" : "default"}
                        label={selectedDevice.online ? "online" : "offline"}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Kunde / Screen</Typography>
                    <Typography variant="body2">
                      {asString(selectedDevice.customer?.companyName)} / {asString(selectedDevice.screen?.name)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Heartbeat</Typography>
                    <Typography variant="body2">{formatDate(selectedDevice.lastHeartbeatAt)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">IP / WAN</Typography>
                    <Typography variant="body2">{asString(selectedDevice.ip)} / {asString(selectedDevice.wanIp)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Versionen</Typography>
                    <Typography variant="body2">App {asString(selectedDevice.appVersion)} | OS {asString(selectedDevice.osVersion)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">CPU</Typography>
                    <Typography variant="body2">{asString(telemetry.cpu)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">RAM</Typography>
                    <Typography variant="body2">{asString(telemetry.memory)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Disk</Typography>
                    <Typography variant="body2">{asString(telemetry.disk)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Temp</Typography>
                    <Typography variant="body2">{asString(telemetry.temp)}</Typography>
                  </Grid>
                </Grid>
              ) : null}
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} mb={1}>
                <Typography variant="h6">Quick Actions</Typography>
              </Stack>
              <Divider sx={{ mb: 1.5 }} />
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {QUICK_ACTIONS.map((item) => (
                  <Button
                    key={item.action}
                    variant="contained"
                    size="small"
                    onClick={() => handleQuickAction(item.action)}
                    disabled={!selectedDeviceId || !!busyAction}
                  >
                    {busyAction === `quick:${item.action}` ? "Läuft..." : item.label}
                  </Button>
                ))}
              </Stack>

              <Typography variant="subtitle2" mt={2} mb={1}>Command Catalog</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {commandCatalog.map((item) => (
                  <Button
                    key={item.name}
                    variant="outlined"
                    size="small"
                    onClick={() => handleCatalogCommand(item.name, item.defaultPayload)}
                    disabled={!selectedDeviceId || !!busyAction}
                  >
                    {busyAction === `catalog:${item.name}` ? "Läuft..." : item.label || item.name}
                  </Button>
                ))}
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>Device-Onboarding Token</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                <TextField
                  label="expiresIn"
                  size="small"
                  value={tokenExpiresIn}
                  onChange={(event) => setTokenExpiresIn(event.target.value)}
                  sx={{ minWidth: 180 }}
                />
                <Button
                  variant="contained"
                  startIcon={<KeyIcon />}
                  onClick={handleGenerateToken}
                  disabled={!selectedDeviceId || !!busyAction}
                >
                  {busyAction === "token" ? "Erzeuge..." : "Token erzeugen"}
                </Button>
              </Stack>

              {generatedToken ? (
                <Box mt={1.5}>
                  <TextField
                    fullWidth
                    label="Token"
                    size="small"
                    value={generatedToken.token || ""}
                    InputProps={{ readOnly: true }}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    label="WebSocket URL"
                    size="small"
                    value={generatedToken.websocketUrl || ""}
                    InputProps={{ readOnly: true }}
                  />
                </Box>
              ) : null}
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} mb={1}>
                <Typography variant="h6">Command-Historie</Typography>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="command-status-label">Status</InputLabel>
                  <Select
                    labelId="command-status-label"
                    value={commandStatusFilter}
                    label="Status"
                    onChange={(event) => setCommandStatusFilter(event.target.value)}
                  >
                    {COMMAND_STATUSES.map((status) => (
                      <MenuItem key={status || "all"} value={status}>{status || "Alle"}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {loadingCommands ? (
                <Box py={2} textAlign="center"><CircularProgress size={24} /></Box>
              ) : null}
              {!loadingCommands && commands.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Keine Commands für dieses Device.</Typography>
              ) : null}

              <Stack spacing={1}>
                {commands.map((command) => (
                  <Paper
                    key={command.id}
                    variant={selectedCommandId === command.id ? "outlined" : "elevation"}
                    sx={{
                      p: 1.25,
                      borderColor: selectedCommandId === command.id ? "primary.main" : "divider",
                    }}
                  >
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                      <Box sx={{ cursor: "pointer" }} onClick={() => setSelectedCommandId(command.id)}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={0.25}>
                          <Typography variant="subtitle2">{command.name}</Typography>
                          <Chip size="small" color={statusColor(command.status)} label={command.status} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block">
                          erstellt: {formatDate(command.createdAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          updated: {formatDate(command.updatedAt)}
                        </Typography>
                      </Box>

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RestartAltIcon />}
                        onClick={() => handleDispatchAgain(command.id)}
                        disabled={!!busyAction}
                      >
                        {busyAction === `dispatch:${command.id}` ? "Sende..." : "Erneut dispatchen"}
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>Statusverlauf (Command Audits)</Typography>
              {loadingCommandDetail ? (
                <Box py={2} textAlign="center"><CircularProgress size={24} /></Box>
              ) : null}
              {!loadingCommandDetail && !selectedCommand ? (
                <Typography variant="body2" color="text.secondary">Kein Command ausgewählt.</Typography>
              ) : null}

              {selectedCommand ? (
                <>
                  <Typography variant="body2" mb={1}>
                    Command <strong>{selectedCommand.name}</strong> ({selectedCommand.id})
                  </Typography>
                  <Stack spacing={1}>
                    {asArray(selectedCommand.audits).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Keine Audit-Einträge.</Typography>
                    ) : null}
                    {asArray(selectedCommand.audits).map((audit) => (
                      <Paper key={audit.id} variant="outlined" sx={{ p: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="subtitle2">{audit.action}</Typography>
                          <Chip size="small" color={statusColor(audit.status)} label={audit.status} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block">
                          at: {formatDate(audit.createdAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          role: {asString(audit.actorRole)}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </>
              ) : null}
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Letzte Device-Audits</Typography>
              <Stack spacing={1}>
                {deviceAudits.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Keine Audits für dieses Device.</Typography>
                ) : null}
                {deviceAudits.slice(0, 20).map((audit) => (
                  <Paper key={audit.id} variant="outlined" sx={{ p: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="subtitle2">{audit.action}</Typography>
                      <Chip size="small" color={statusColor(audit.status)} label={audit.status} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">
                      at: {formatDate(audit.createdAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      role: {asString(audit.actorRole)}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      <Dialog open={liveFeedOpen} onClose={handleCloseLiveFeed} maxWidth="md" fullWidth>
        <DialogTitle>Live-Feed (Screenshot Pull)</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Status: <strong>{liveFeedStatus || "-"}</strong>
              {liveFeedCommandId ? ` | Command: ${liveFeedCommandId}` : ""}
              {liveFeedUpdatedAt ? ` | Updated: ${formatDate(liveFeedUpdatedAt)}` : ""}
            </Typography>

            {liveFeedError ? <Alert severity="error">{liveFeedError}</Alert> : null}
            {liveFeedLoading ? (
              <Box py={1} textAlign="center">
                <CircularProgress size={22} />
              </Box>
            ) : null}

            {liveFeedImageUrl ? (
              <Box
                component="img"
                src={liveFeedImageUrl}
                alt="Device Live Feed"
                sx={{
                  width: "100%",
                  maxHeight: 520,
                  objectFit: "contain",
                  borderRadius: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  bgcolor: "black",
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Noch kein Screenshot verfügbar. Es wird weiter gepollt.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => void handleOpenLiveFeed()} disabled={!selectedDeviceId || liveFeedLoading}>
            Neu anfordern
          </Button>
          <Button onClick={() => void refreshLiveFeed()} disabled={!selectedDeviceId || liveFeedLoading}>
            Aktualisieren
          </Button>
          <Button onClick={handleCloseLiveFeed}>Schließen</Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}
